import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ZoomIn, Move, RotateCw, Sun, Camera, Crosshair, RefreshCw,
  FlipHorizontal, Play, Ruler, ArrowRight, Type,
  Circle, Square, PenTool, Eye, Link2, Grid3X3, Grid2X2,
  ChevronDown, X, Spline, Cable, ChevronRight, ScanLine, Tags,
  Search, Layers, Box, PanelTop, Contrast, Maximize, Route,
  Orbit, RectangleHorizontal, BetweenHorizontalEnd, BetweenVerticalEnd,
  PanelLeft,PanelLeftClose,DraftingCompass,Activity
} from 'lucide-react';

import './LeftToolbar.css';
import { commandsManager } from '../App';

const STORAGE_KEY = 'OHIF_LEFT_TOOLBAR_COLLAPSED';

const toggleTools = [
  'Zoom', 'Pan', 'WindowLevel', 'Crosshairs', 'TextMarker',
  'Length', 'Bidirectional', 'ArrowAnnotate', 'EllipticalROI',
  'RectangleROI', 'CircleROI', 'PlanarFreehandROI', 'SplineROI',
  'LivewireContour', 'StackScroll', 'Probe', 'Angle', 'CobbAngle',
  'CalibrationLine', 'Magnify', 'WindowLevelRegion'
];

const defaultCollapsed: Record<string, boolean> = {
  main: false,
  measure: true,
  common: true,
  layout: true,
};

const groups = [
  {
    id: 'main',
    title: 'Main',
    tools: [
      { icon: ZoomIn, label: 'Zoom', cmd: 'Zoom' },
      { icon: Move, label: 'Di chuyển', cmd: 'Pan' },
      { icon: Sun, label: 'Ánh sáng / Window Level', cmd: 'WindowLevel' },
      { icon: Camera, label: 'Chụp ảnh', cmd: 'Capture' },
      { icon: Crosshair, label: 'Crosshair', cmd: 'Crosshairs' },
      { icon: Type, label: 'Text Marker', cmd: 'TextMarker' },
    ],
  },
  {
    id: 'measure',
    title: 'Đo lường / Annotation',
    tools: [
      { icon: Ruler, label: 'Đo chiều dài', cmd: 'Length' },
      { icon: X, label: 'Bidirectional', cmd: 'Bidirectional' },
      { icon: ArrowRight, label: 'Mũi tên ghi chú', cmd: 'ArrowAnnotate' },
      { icon: Circle, label: 'Ellipse ROI', cmd: 'EllipticalROI' },
      { icon: Square, label: 'Rectangle ROI', cmd: 'RectangleROI' },
      { icon: Circle, label: 'Circle ROI', cmd: 'CircleROI' },
      { icon: PenTool, label: 'Freehand ROI', cmd: 'PlanarFreehandROI' },
      { icon: Spline, label: 'Spline ROI', cmd: 'SplineROI' },
      { icon: Cable, label: 'Livewire Contour', cmd: 'LivewireContour' },
      { icon: DraftingCompass, label: 'Đo góc', cmd: 'Angle' },
      { icon: Activity , label: 'Cobb Angle', cmd: 'CobbAngle' },
    ],
  },
  {
    id: 'common',
    title: 'Common',
    tools: [
      { icon: RefreshCw, label: 'Reset', cmd: 'Reset' },
      { icon: RotateCw, label: 'Xoay phải', cmd: 'RotateRight' },
      { icon: FlipHorizontal, label: 'Lật ngang', cmd: 'FlipHorizontal' },
      { icon: Link2, label: 'Đồng bộ', cmd: 'Sync' },
      { icon: RectangleHorizontal, label: 'Reference Lines', cmd: 'ReferenceLines' },
      { icon: Layers, label: 'Overlay', cmd: 'Overlay' },
      { icon: Eye, label: 'Stack Scroll', cmd: 'StackScroll' },
      { icon: Contrast, label: 'Đảo màu', cmd: 'Invert' },
      { icon: Crosshair, label: 'Probe', cmd: 'Probe' },
      { icon: Play, label: 'Cine', cmd: 'Cine' },
      { icon: Ruler, label: 'Calibration Line', cmd: 'CalibrationLine' },
      { icon: Tags, label: 'DICOM Tag Browser', cmd: 'DicomTagBrowser' },
      { icon: Search, label: 'Kính lúp', cmd: 'Magnify' },
      { icon: Sun, label: 'Window Level Region', cmd: 'WindowLevelRegion' },
    ],
  },
  {
    id: 'layout',
    title: 'Layout',
    tools: [
      { icon: Square, label: 'Layout 1x1', cmd: 'Layout1x1' },
      { icon: BetweenHorizontalEnd, label: 'Layout 1x2', cmd: 'Layout1x2' },
      { icon: BetweenVerticalEnd, label: 'Layout 2x1', cmd: 'Layout2x1' },
      { icon: Grid2X2, label: 'Layout 2x2', cmd: 'Layout2x2' },
      { icon: Grid3X3, label: 'Layout 3x3', cmd: 'Layout3x3' },
      { icon: Box, label: 'MPR', cmd: 'MPR' },
      { icon: PanelTop, label: '3D 4 Up', cmd: 'Layout3D4Up' },
      { icon: Orbit, label: '3D Main', cmd: 'Layout3DMain' },
      { icon: PanelLeft, label: 'Axial Primary', cmd: 'primaryAxial' },
      { icon: Maximize, label: '3D Only', cmd: 'ThreeDOnly' },
      { icon: PanelLeftClose, label: 'Primary 3D', cmd: 'primary3D' },
      { icon: Layers, label: 'Frame', cmd: 'Frame' },
    ],
  },
];

export default function LeftToolbar() {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [activeCmd, setActiveCmd] = useState<string | null>(null);

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : defaultCollapsed;
    } catch {
      return defaultCollapsed;
    }
  });

  useEffect(() => {
    let ro: ResizeObserver | null = null;

    const timer = setInterval(() => {
      const el = document.getElementById('viewerLayoutResizableLeftPanel');

      if (el) {
        setHost(el);
        setIsNarrow(el.offsetWidth < 210);
        clearInterval(timer);

        ro = new ResizeObserver(() => {
          setIsNarrow(el.offsetWidth < 210);
        });

        ro.observe(el);
      }
    }, 300);

    return () => {
      clearInterval(timer);
      if (ro) ro.disconnect();
    };
  }, []);

  function toggleGroup(id: string) {
    const next = {
      ...collapsed,
      [id]: !collapsed[id],
    };

    setCollapsed(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }

  function tryRun(names: string[], options?: any) {
    for (const name of names) {
      try {
        commandsManager.runCommand(name, options);
        return true;
      } catch {}
    }

    return false;
  }

  function cancelTool(cmd: string) {
    tryRun(['setToolPassive'], {
      toolName: cmd,
    });

    setActiveCmd(null);
  }

  function setLayout(row: number, col: number) {
    tryRun(['setViewportGridLayout'], {
      numRows: row,
      numCols: col,
    });
  }

  function runTool(cmd: string) {
    if (toggleTools.includes(cmd) && activeCmd === cmd) {
      cancelTool(cmd);
      return;
    }

    switch (cmd) {
      case 'Reset':
        tryRun(['resetViewport', 'resetViewports']);
        setActiveCmd(null);
        break;

      case 'RotateRight':
        tryRun(['rotateViewportCW', 'rotateViewport', 'rotateRight'], {
          rotation: 90,
        });
        setActiveCmd(null);
        break;

      case 'FlipHorizontal':
        tryRun(['flipViewportHorizontal', 'flipHorizontal']);
        setActiveCmd(null);
        break;

      case 'Cine':
        tryRun(['toggleCine', 'cineStart', 'setCine']);
        setActiveCmd(activeCmd === 'Cine' ? null : 'Cine');
        break;

      case 'Invert':
        tryRun(['invertViewport', 'toggleInvert']);
        setActiveCmd(null);
        break;

      case 'Layout1x1':
        setLayout(1, 1);
        setActiveCmd(cmd);
        break;

      case 'Layout1x2':
        setLayout(1, 2);
        setActiveCmd(cmd);
        break;

      case 'Layout2x1':
        setLayout(2, 1);
        setActiveCmd(cmd);
        break;

      case 'Layout2x2':
        setLayout(2, 2);
        setActiveCmd(cmd);
        break;

      case 'Layout3x3':
        setLayout(3, 3);
        setActiveCmd(cmd);
        break;

      case 'DicomTagBrowser':
        tryRun(['showDicomTagBrowser', 'toggleDicomTagBrowser']);
        setActiveCmd(null);
        break;

      case 'Capture':
        tryRun(['showDownloadViewportModal', 'captureViewport', 'downloadViewportScreenshot']);
        setActiveCmd(null);
        break;
      case 'MPR':
          tryRun(
              ['setHangingProtocol'],
              {
                  protocolId:'mpr'
              }
          );
          break;
      case 'Layout3D4Up':
        tryRun(
            ['setHangingProtocol'],
            {
                protocolId:'fourUp'
            }
        );
        setActiveCmd(cmd);
        break;
    case 'Layout3DMain':
        tryRun(
            ['setHangingProtocol'],
            {
                protocolId:'main3D'
            }
        );
        setActiveCmd(cmd);
        break;
    case 'primaryAxial':
        tryRun(
            ['setHangingProtocol'],
            {
                protocolId:'primaryAxial'
            }
        );
        setActiveCmd(cmd);
        break;
    case 'ThreeDOnly':
        tryRun(
            ['setHangingProtocol'],
            {
                protocolId:'only3D'
            }
        );
        setActiveCmd(cmd);
        break;
    case 'primary3D':
        tryRun(
            ['setHangingProtocol'],
            {
                protocolId:'primary3D'
            }
        );
        setActiveCmd(cmd);
        break;
    case 'Frame':
        tryRun(
            ['setHangingProtocol'],
            {
                protocolId:'frame-view'
            }
        );
        setActiveCmd(cmd);
        break;
      default:
        tryRun(['setToolActive'], {
          toolName: cmd,
        });

        if (toggleTools.includes(cmd)) {
          setActiveCmd(cmd);
        } else {
          setActiveCmd(null);
        }

        break;
    }
  }

  if (!host || isNarrow) return null;

  return createPortal(
    <div id="leftToolPanel">
      {groups.map(group => {
        const close = collapsed[group.id];

        return (
          <div className={`toolGroup ${close ? 'collapsed' : ''}`} key={group.id}>
            <button
              className="toolTitleMini"
              type="button"
              title={group.title}
              onClick={() => toggleGroup(group.id)}
            >
              {close ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
              <span>{group.title}</span>
            </button>

            {!close && (
              <div className="toolGrid">
                {group.tools.map(tool => {
                  const ToolIcon = tool.icon;
                  const isActive = activeCmd === tool.cmd;

                  return (
                    <button
                      key={`${group.id}_${tool.cmd}`}
                      className={`toolButton ${isActive ? 'active' : ''}`}
                      type="button"
                      title={tool.label}
                      aria-label={tool.label}
                      onClick={() => runTool(tool.cmd)}
                    >
                      <ToolIcon size={17} strokeWidth={1.8} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>,
    host
  );
}
